import Logout from '@mui/icons-material/Logout'
import Person2OutlinedIcon from '@mui/icons-material/Person2Outlined'
import { Divider, Drawer, IconButton, List, Stack, useMediaQuery, useTheme } from '@mui/material'
import { FC } from 'react'
import { useAuth } from 'react-oidc-context'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFoUserStorageInfoApiV1UserFolderFileObjectStorageInfoGet } from '../../../api/gen/fileGW'
import BellIcon from '../../../assets/icons/Bell.svg'
import CalendarIcon from '../../../assets/icons/Calendar.svg'
import CloseIcon from '../../../assets/icons/Close.svg?react'
import GearIcon from '../../../assets/icons/Gear.svg'
import InboxAltIcon from '../../../assets/icons/InboxAlt.svg'
import LettersCaseIcon from '../../../assets/icons/LettersCase.svg'
import NewFileIcon from '../../../assets/icons/NewFile.svg'
import PenSquareIcon from '../../../assets/icons/PenSquare.svg'
import PlusIcon from '../../../assets/icons/Plus.svg'
import SquareListIcon from '../../../assets/icons/SquareList.svg'
import { APP_TOP_INDENT, APP_TOP_INDENT_MOBILE } from '../../../constants/app.indents'
import {
    ASSISTANT_SHOP_ROUTE,
    CALENDAR_ROUTE,
    DISK_ROUTE,
    NOTIFICATIONS_ROUTE,
    PROFILE_ROUTE,
    RECORDS_ROUTE,
    ROOT_ROUTE,
    SETTING_ROUTE,
    TERMS_OF_USE_ROUTE,
} from '../../../constants/routing.constants'
import { useNotificationsStore } from '../../../store/storeNotifications'
import { useProfileStore } from '../../../store/storeProfile'
import { useWrapperStore } from '../../../store/storeWrapper'
import { NotificationsBadge } from '../../NotificationsBadge/NotificationsBadge'
import { NavigationButton } from './components/NavigationButton'
import { ProjectsList } from './components/ProjectsList'
import { RequestsList } from './components/RequestsList'
import { SidebarDivider } from './components/SidebarDivider'
import { SidebarTooltip } from './components/SidebarTooltip'
import { StorageProgressBar } from './components/StorageProgressBar'

const DRAWER_WIDTH = 292

export const NavigationSidebar: FC = () => {
    const { breakpoints } = useTheme()
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const auth = useAuth()

    const isMobile = useMediaQuery(breakpoints.down('sm'))

    const {
        isTempUser,
        setOpenDialogAddBotToMeet,
        isOpenSidebar,
        setOpenSidebar,
        stepOnboarding,
        dialogsOpen,
    } = useWrapperStore((state) => state)
    const { userTariff } = useProfileStore()
    const { numberOfUnreadNotifications } = useNotificationsStore((state) => state)

    const { data: storageData } = useFoUserStorageInfoApiV1UserFolderFileObjectStorageInfoGet()

    const handleCloseSidebar = () => setOpenSidebar(false)

    const handleAddBotToMeet = () => {
        setOpenDialogAddBotToMeet(true)
        handleCloseSidebar()
    }

    const handleAddRecord = () => {
        navigate(`${RECORDS_ROUTE}/upload`)
        handleCloseSidebar()
    }

    const handleNavigate = (href: string) => () => {
        navigate(href)
    }

    const handleLogout = () => {
        auth.signoutRedirect()
    }

    const isActiveButton = (mainPath: string) => pathname === mainPath

    const isOpen = !isMobile || isOpenSidebar
    const isStepOnboarding = stepOnboarding === 2 || stepOnboarding === 4

    return (
        <Drawer
            onClose={handleCloseSidebar}
            variant={isMobile ? 'temporary' : 'persistent'}
            anchor="left"
            open={isOpen}
            sx={{
                zIndex: (theme) => (!isMobile && isStepOnboarding ? theme.zIndex.drawer + 1 : theme.zIndex.drawer),
                '& .MuiDrawer-paper': {
                    zIndex: (theme) =>
                        !isMobile && isStepOnboarding ? theme.zIndex.drawer + 1 : theme.zIndex.drawer,
                    // position: 'relative'
                },
                // zIndex: !isMobile && stepOnboarding === 3 && dialogsOpen === false ? 1304 : 0,
                position: isMobile ? 'fixed' : 'relative',
                width: isMobile ? 'unset' : DRAWER_WIDTH,
                height: '100%',
            }}
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: isStepOnboarding ? '#7f7f7f' : '#fff',
                        position: isMobile ? 'absolute' : 'sticky',
                        top: isMobile ? 0 : `${APP_TOP_INDENT - 16}px`,
                        height: isMobile ? '100%' : `calc(100dvh - ${isMobile ? APP_TOP_INDENT_MOBILE : APP_TOP_INDENT}px)`,
                        width: isMobile ? DRAWER_WIDTH : '100%',
                        p: 0,
                        pt: 2,
                        borderRadius: isMobile ? '0 28px 0 0' : '28px',
                        border: 'none',
                        boxShadow: 'none',
                        overflow: 'hidden',
                        // zIndex: 1,
                    },
                },
            }}>
            {isMobile && isOpen && (
                <IconButton
                    size="medium"
                    sx={{
                        position: 'fixed',
                        top: '16px',
                        left: `${DRAWER_WIDTH + 16}px`,
                        backgroundColor: '#FFFFFF',
                        ':hover': {
                            backgroundColor: '#FFFFFF',
                        },
                    }}
                    onClick={handleCloseSidebar}>
                    <CloseIcon color="#5B6072" />
                </IconButton>
            )}
            <List sx={{ p: '0 16px', backgroundColor: isStepOnboarding ? '#7f7f7f' : '#fff' }}>
                <NavigationButton
                    onClick={handleNavigate(ROOT_ROUTE)}
                    isActive={isStepOnboarding ? false : isActiveButton(ROOT_ROUTE)}
                    startIcon={<PenSquareIcon />}>
                    Новый запрос
                </NavigationButton>
                <NavigationButton
                    disabled={isTempUser || stepOnboarding !== 100}
                    onClick={handleNavigate(CALENDAR_ROUTE)}
                    isActive={isActiveButton(CALENDAR_ROUTE)}
                    startIcon={<CalendarIcon />}
                    additionalContent={
                        <SidebarTooltip
                            titleTariff={
                                !userTariff?.tariff?.restrictions?.is_recording_available
                                    ? 'Доступно на уровне «Профи»'
                                    : ''
                            }
                            title="Подключить бота к встрече"
                            placement={isMobile ? 'top-start' : 'right'}>
                            <IconButton
                                size="medium"
                                onClick={handleAddBotToMeet}
                                disabled={
                                    isTempUser ||
                                    !userTariff?.tariff?.restrictions?.is_recording_available ||
                                    stepOnboarding !== 100
                                }>
                                <PlusIcon />
                            </IconButton>
                        </SidebarTooltip>
                    }>
                    Календарь
                </NavigationButton>
                <NavigationButton
                    disabled={isTempUser}
                    onClick={handleNavigate(RECORDS_ROUTE)}
                    isActive={isActiveButton(RECORDS_ROUTE)}
                    startIcon={<SquareListIcon />}
                    additionalContent={
                        <SidebarTooltip
                            titleTariff={
                                !userTariff?.tariff?.restrictions?.is_recording_available
                                    ? 'Доступно на уровне «Профи»'
                                    : ''
                            }
                            title="Добавить запись для расшифровки"
                            placement={isMobile ? 'top-start' : 'right'}>
                            <IconButton
                                onClick={handleAddRecord}
                                disabled={
                                    isTempUser ||
                                    !userTariff?.tariff?.restrictions?.is_recording_available ||
                                    stepOnboarding !== 100
                                }>
                                <PlusIcon />
                            </IconButton>
                        </SidebarTooltip>
                    }>
                    Записи встреч
                </NavigationButton>
                <NavigationButton
                    isOnboardingTooltip={true}
                    disabled={isTempUser}
                    onClick={handleNavigate(ASSISTANT_SHOP_ROUTE)}
                    isActive={isActiveButton(ASSISTANT_SHOP_ROUTE)}
                    startIcon={<LettersCaseIcon />}>
                    Все ассистенты
                </NavigationButton>
                <NavigationButton
                    disabled={isTempUser}
                    onClick={handleNavigate(DISK_ROUTE)}
                    isActive={isActiveButton(DISK_ROUTE)}
                    startIcon={<InboxAltIcon />}
                    additionalContent={
                        storageData && (
                            <StorageProgressBar
                                disabled={stepOnboarding === 2 || stepOnboarding === 4}
                                storageSizeMb={storageData.storage_size_mb}
                                ragSizeMb={storageData.rag_size_mb}
                            />
                        )
                    }>
                    Мой диск
                </NavigationButton>
            </List>
            <Divider sx={{ m: '8px 16px', mb: 0 }} />
            <Stack sx={{ overflow: 'auto', pb: 2, pt: 1 }}>
                {!isTempUser && isMobile && (
                    <>
                        <List sx={{ p: '0 16px' }}>
                            <NavigationButton
                                onClick={handleNavigate(NOTIFICATIONS_ROUTE)}
                                startIcon={
                                    <NotificationsBadge
                                        count={numberOfUnreadNotifications}
                                        showZero={false}>
                                        <BellIcon />
                                    </NotificationsBadge>
                                }>
                                Уведомления и события
                            </NavigationButton>
                            <NavigationButton
                                onClick={handleNavigate(SETTING_ROUTE)}
                                isActive={isActiveButton(SETTING_ROUTE)}
                                startIcon={<GearIcon />}>
                                Глобальные настройки
                            </NavigationButton>
                            <NavigationButton
                                onClick={handleNavigate(PROFILE_ROUTE)}
                                isActive={isActiveButton(PROFILE_ROUTE)}
                                startIcon={<Person2OutlinedIcon sx={{ width: 20, height: 20 }} />}>
                                Профиль
                            </NavigationButton>
                            <NavigationButton
                                onClick={handleNavigate(TERMS_OF_USE_ROUTE)}
                                isActive={isActiveButton(TERMS_OF_USE_ROUTE)}
                                startIcon={<NewFileIcon />}>
                                Условия использования
                            </NavigationButton>
                            <NavigationButton
                                onClick={handleLogout}
                                startIcon={<Logout sx={{ width: 20, height: 20 }} />}>
                                Выйти из аккаунта
                            </NavigationButton>
                        </List>
                        <SidebarDivider />
                    </>
                )}
                <ProjectsList />
                <RequestsList />
            </Stack>
        </Drawer>
    )
}
